/**
 * `/teacher/grading` — list of AI-drafted feedback awaiting teacher
 * review. The page intentionally shows pseudonymous student names only;
 * the redaction vault is server-side and never touched from the UI.
 *
 * Required UX guardrails (S1-011):
 *
 *   - Every row carries the AI-draft labels: "AI Draft", "Needs Teacher
 *     Review", "Not Published to LMS".
 *   - The page header makes the draft-only nature explicit so a teacher
 *     does not assume the AI is grading on their behalf.
 *   - Strings "Final" and "Auto-published" must NEVER appear here.
 */

import { useApiClient } from '../../api/ApiClientProvider';
import { useFetch } from '../../api/useFetch';
import { useSession } from '../../auth/SessionProvider';
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from '../../components/States';
import { AiDraftLabels } from '../../components/AiDraftLabels';

export function Grading() {
  const client = useApiClient();
  const { session } = useSession();
  const token = session?.token ?? '';
  const state = useFetch(
    () => client.listGradingDrafts(token),
    [client, token],
  );

  if (state.status === 'loading') {
    return <LoadingState label="Loading grading drafts…" />;
  }
  if (state.status === 'error') {
    return <ErrorState error={state.error} />;
  }
  if (state.data.length === 0) {
    return (
      <EmptyState
        title="No grading drafts yet"
        description="The Grading-Draft Agent ships in Sprint 2. Run a mock LMS sync first to seed sample submissions."
      />
    );
  }

  return (
    <section className="page" data-testid="teacher-grading">
      <header className="page__header">
        <h1>Grading drafts</h1>
        <p className="callout">
          Everything below is <strong>draft AI feedback</strong>. Teacher review
          is required before any feedback is shared with a student. Grades are
          never automatically posted, and the LMS publishing path is disabled
          in this MVP.
        </p>
      </header>

      <table className="table" aria-label="Grading drafts">
        <thead>
          <tr>
            <th scope="col">Submission</th>
            <th scope="col">Course</th>
            <th scope="col">Student</th>
            <th scope="col">Status</th>
            <th scope="col">Updated</th>
          </tr>
        </thead>
        <tbody>
          {state.data.map((draft) => (
            <tr key={draft.id} data-testid="grading-row">
              <td>{draft.assignmentTitle}</td>
              <td>{draft.courseName}</td>
              <td>
                <code aria-label={`Pseudonymous student ${draft.studentPseudonym}`}>
                  {draft.studentPseudonym}
                </code>
              </td>
              <td>
                <AiDraftLabels status={draft.status} />
              </td>
              <td>
                <time dateTime={draft.updatedAt}>
                  {formatDate(draft.updatedAt)}
                </time>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
  } catch {
    return iso;
  }
}
